import { SpanStatusCode } from '@opentelemetry/api';
import * as Sentry from '@sentry/nextjs';
import Layout from 'components/layout';
import BuyCoinsForm from 'components/premium/BuyCoins';
import { AnonymousUser } from 'components/premium/PersonSummary';
import Anchor from 'components/typography/anchor';
import coinStacks from 'images/icons/coin-stacks.svg';
import coinShareImg from 'images/shares/coins.png';
import { otelApi } from 'lib/instrumentation'; // Need this to get the instrumentation initialized
import { getExistingCustomer } from 'lib/stripe';
import Img from 'next/image';
import PropTypes from 'prop-types';
import {
  BankAccountPropType,
  CardPaymentMethodPropType,
  CashAppPropType,
  LinkPropType,
} from 'utilities/propTypes';
import { auth } from '@/auth';

const BuyCoins = ({ customer, paymentMethod, clientSecret = null }) => {
  return (
    <Layout
      title="Buy Resistbot Coins"
      description="Top off your Resistbot coins to promote your messages to more people, buy more letters, and to help the 'bot run."
      image={coinShareImg}
      imageAltText='Illustration of a gold resistbot coin with overlay text that reads, "Buy resistbot coins: promote campaigns, buy more letters, support the bot! Text buy to 50409."'
    >
      <section>
        <div className="flex flex-col items-center gap-2 text-center">
          <Img
            src={coinStacks}
            width="120"
            height="120"
            alt={'Small stacks of Resistbot coins'}
            priority={true}
          />
          <h1>Buy Coins</h1>
          <p className="mb-4 max-w-prose text-sm">
            Use coins to pay for faxes, real mailed physical letters, or to
            promote campaigns!{' '}
            <Anchor className="anchor-text" href="/premium">
              Go premium
            </Anchor>{' '}
            for double coins for every&nbsp;dollar!
          </p>
        </div>
        {!customer ? (
          <AnonymousUser keyword={'buy'} />
        ) : (
          <BuyCoinsForm
            customer={customer}
            clientSecret={clientSecret}
            paymentMethod={paymentMethod}
          />
        )}
      </section>
    </Layout>
  );
};

export const getServerSideProps = Sentry.wrapGetServerSidePropsWithSentry(
  async (ctx) => {
    const activeSpan = otelApi.trace.getActiveSpan();

    const session = await auth(ctx);
    const user = session?.user;
    const userId = user?.id;
    const stripeCustomerId = user?.stripe?.customerId;

    Sentry.addBreadcrumb({
      category: 'GSSP:/premium/coins',
      message: 'Incoming data',
      data: { user: session?.user },
    });

    activeSpan.setAttributes({
      contact_uuid: userId,
    });
    Sentry.setUser({ id: userId });

    const { customer, paymentMethod, setupIntent, attempts } =
      await getExistingCustomer({ uuid: userId, customerId: stripeCustomerId });

    Sentry.addBreadcrumb({
      category: 'GSSP:/premium/coins',
      message: 'Located customer details',
      data: {
        customer,
        paymentMethod,
        setupIntent,
        traceId: activeSpan.spanContext().traceId,
      },
    });

    let sentryId = null;

    if (userId) {
      if (!customer) {
        const errorMessage = 'No Stripe Customer found for user';
        sentryId = Sentry.captureException(
          new Error('No Stripe Customer found for user')
        );
        activeSpan.setStatus({
          code: SpanStatusCode.ERROR,
          message: errorMessage,
        });
      }
    }

    activeSpan.setAttributes({
      'stripe.customer.id': stripeCustomerId || customer?.id,
      'stripe.paymentMethod': paymentMethod?.id,
      'stripe.setupIntent': setupIntent?.id,
      'stripe.should_collect_payment_method': setupIntent?.clientSecret,
      'stripe.search.attempts': attempts.search.attempts,
      'stripe.search.elapsedTime': attempts.search.time,
      'stripe.direct.attempts': attempts.direct.attempts,
      'stripe.direct.elapsedTime': attempts.direct.time,
      'stripe.linked': customer ? true : null,
      'sentry.error': sentryId,
    });

    return {
      props: {
        customer,
        paymentMethod,
        clientSecret: setupIntent.clientSecret,
      },
    };
  }
);

BuyCoins.propTypes = {
  customer: PropTypes.shape({
    id: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    uuid: PropTypes.string.isRequired,
  }),
  paymentMethod: PropTypes.oneOfType([
    CardPaymentMethodPropType,
    BankAccountPropType,
    LinkPropType,
    CashAppPropType,
  ]),
  clientSecret: PropTypes.string,
};

export default BuyCoins;
